import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArchiveIcon,
  BackIcon,
  CameraIcon,
  CloseIcon,
  PlusIcon,
  SendIcon,
  VideoIcon,
} from '../components/Icons';
import { StoryThumbnail } from '../components/StoryThumbnail';
import { AutoplayVideo, VideoRecorder } from '../components/VideoRecorder';
import {
  api,
  type FamilyStory,
  type Group,
  type StoryDetail,
  type StoryThread,
} from '../lib/api';

const REACTIONS = ['❤️', '😂', '🥹', '👏', '😍'] as const;

export function StoriesPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const storyId = params.get('story');
  const requestedAudience = params.get('audience') ?? undefined;
  const composing = params.get('compose') === '1';
  const [scope, setScope] = useState<'active' | 'archive'>('active');
  const [stories, setStories] = useState<FamilyStory[]>([]);
  const [audiences, setAudiences] = useState<Group[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<StoryDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const loadStories = useCallback(async (nextScope = scope) => {
    setLoading(true);
    try {
      const result = await api.stories(nextScope);
      setStories(result.stories);
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not load stories.');
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => { void loadStories(); }, [loadStories]);

  const loadAudiences = useCallback(async () => {
    const result = await api.storySetup();
    setAudiences(result.audiences);
    return result.audiences;
  }, []);

  useEffect(() => { void loadAudiences().catch(() => undefined); }, [loadAudiences]);

  useEffect(() => {
    if (!storyId) {
      setDetail(null);
      setDetailError(null);
      return;
    }
    let current = true;
    setDetailLoading(true);
    setDetailError(null);
    void api.story(storyId, requestedAudience).then((result) => {
      if (current) setDetail(result);
    }).catch((caught) => {
      if (current) setDetailError(caught instanceof Error ? caught.message : 'This story is unavailable.');
    }).finally(() => {
      if (current) setDetailLoading(false);
    });
    return () => { current = false; };
  }, [requestedAudience, storyId]);

  const setQuery = useCallback((changes: Record<string, string | null>) => {
    const next = new URLSearchParams(location.search);
    for (const [key, value] of Object.entries(changes)) {
      if (value === null) next.delete(key);
      else next.set(key, value);
    }
    const query = next.toString();
    navigate(`/stories${query ? `?${query}` : ''}`, { replace: true });
  }, [location.search, navigate]);

  const openStory = (id: string) => setQuery({ story: id, audience: null, compose: null });
  const closeLayer = () => setQuery({ story: null, audience: null, compose: null });
  const currentIndex = storyId ? stories.findIndex((story) => story.id === storyId) : -1;
  const markLocalViewed = useCallback((id: string, audienceId: string) => {
    setStories((current) => current.map((story) => story.id === id
      ? {
          ...story,
          audiences: story.audiences.map((audience) => audience.id === audienceId && !audience.viewed
            ? { ...audience, viewed: true }
            : audience),
        }
      : story));
  }, []);

  return (
    <>
      <header className="app-header stories-header">
        <div>
          <p className="eyebrow">The everyday stuff</p>
          <h1>Family Stories</h1>
        </div>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => setQuery({ compose: '1', story: null, audience: null })}>
          <PlusIcon />Add story
        </button>
      </header>

      <div className="page stories-page">
        <section className="stories-intro">
          <div><span>24h</span></div>
          <div>
            <strong>Catch up today. Keep it forever.</strong>
            <p>New updates stay up front for a day, then settle into your private family archive.</p>
          </div>
        </section>

        <div className="stories-scope" role="tablist" aria-label="Story view">
          <button
            type="button"
            role="tab"
            aria-selected={scope === 'active'}
            className={scope === 'active' ? 'active' : ''}
            onClick={() => { setScope('active'); void loadStories('active'); }}
          >Today</button>
          <button
            type="button"
            role="tab"
            aria-selected={scope === 'archive'}
            className={scope === 'archive' ? 'active' : ''}
            onClick={() => { setScope('archive'); void loadStories('archive'); }}
          ><ArchiveIcon />Archive</button>
        </div>

        {error && <div className="banner error"><p>{error}</p><button type="button" className="btn btn-sm" onClick={() => void loadStories()}>Try again</button></div>}

        {loading ? <div className="center-fill"><div className="spinner" role="status" aria-label="Loading stories" /></div> : stories.length === 0 ? (
          <section className="stories-empty">
            <span><CameraIcon /></span>
            <h2>{scope === 'active' ? 'No new stories today' : 'Your story archive starts here'}</h2>
            <p>{scope === 'active' ? 'A quick grandkid update is all it takes.' : 'Stories remain here after they leave Today.'}</p>
            <button type="button" className="btn btn-primary" onClick={() => setQuery({ compose: '1' })}>Share the first one</button>
          </section>
        ) : (
          <section className="story-library" aria-label={scope === 'active' ? 'Today’s stories' : 'Story archive'}>
            {stories.map((story) => (
              <button type="button" className="story-library-card" key={story.id} onClick={() => openStory(story.id)}>
                <span className="story-library-media">
                  <StoryThumbnail story={story} size={640} />
                  {story.isVideo && <span className="story-video-mark"><VideoIcon /></span>}
                  {story.active && story.audiences.some((audience) => !audience.viewed) && !story.isMine && <span className="story-new-mark">New</span>}
                </span>
                <span className="story-library-copy">
                  <span className="story-avatar">{initials(story.authorName)}</span>
                  <span><strong>{story.isMine ? 'Your story' : story.authorName}</strong><small>{timeAgo(story.createdAt)} · {story.audiences.map((audience) => audience.label).join(', ')}</small></span>
                </span>
                {story.caption && <span className="story-library-caption">{story.caption}</span>}
              </button>
            ))}
          </section>
        )}
      </div>

      {composing && (
        <StoryComposer
          audiences={audiences}
          reloadAudiences={loadAudiences}
          onClose={closeLayer}
          onCreated={(story) => {
            setScope('active');
            setStories((current) => [story, ...current.filter((item) => item.id !== story.id)]);
            setQuery({ compose: null, story: story.id, audience: null });
          }}
        />
      )}

      {detailLoading && <div className="story-layer-loading"><div className="spinner" role="status" aria-label="Opening story" /></div>}
      {detailError && <div className="story-layer-error" role="dialog" aria-modal="true"><div><h2>Story unavailable</h2><p>{detailError}</p><button type="button" className="btn btn-primary" onClick={closeLayer}>Back to stories</button></div></div>}
      {detail && storyId === detail.story.id && (
        <StoryViewer
          detail={detail}
          position={currentIndex}
          total={stories.length}
          onClose={closeLayer}
          onPrevious={currentIndex > 0 ? () => openStory(stories[currentIndex - 1]!.id) : undefined}
          onNext={currentIndex >= 0 && currentIndex < stories.length - 1 ? () => openStory(stories[currentIndex + 1]!.id) : closeLayer}
          onAudienceChange={(audienceId) => setQuery({ audience: audienceId })}
          onThreadChange={(thread) => setDetail((current) => current ? { ...current, thread } : current)}
          onViewed={markLocalViewed}
        />
      )}
    </>
  );
}

function StoryComposer({ audiences, reloadAudiences, onClose, onCreated }: {
  audiences: Group[] | null;
  reloadAudiences: () => Promise<Group[]>;
  onClose: () => void;
  onCreated: (story: FamilyStory) => void;
}) {
  const photoInput = useRef<HTMLInputElement>(null);
  const videoInput = useRef<HTMLInputElement>(null);
  const libraryInput = useRef<HTMLInputElement>(null);
  const [recorderOpen, setRecorderOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [caption, setCaption] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [availableAudiences, setAvailableAudiences] = useState<Group[]>(audiences ?? []);
  const [audienceLoading, setAudienceLoading] = useState(false);
  const [audienceError, setAudienceError] = useState<string | null>(null);
  const [step, setStep] = useState<'pick' | 'review' | 'audience'>('pick');
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!file) { setPreview(null); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    if (audiences) setAvailableAudiences(audiences);
  }, [audiences]);

  const choose = (files: FileList | null) => {
    const chosen = files?.[0];
    if (!chosen) return;
    if (!chosen.type.startsWith('image/') && !chosen.type.startsWith('video/')) {
      setError('Choose a photo or video.');
      return;
    }
    if (chosen.size > 512 * 1024 * 1024) {
      setError('That video is larger than 512 MB. Shorten it and try again.');
      return;
    }
    setFile(chosen);
    setStep('review');
    setError(null);
  };

  const toggle = (id: string) => setSelected((current) => current.includes(id)
    ? current.filter((item) => item !== id)
    : [...current, id]);

  const refreshAudiences = async () => {
    setAudienceLoading(true);
    setAudienceError(null);
    try {
      const fresh = await reloadAudiences();
      setAvailableAudiences(fresh);
      setSelected((current) => current.filter((id) => fresh.some((audience) => audience.id === id)));
    } catch (caught) {
      setAudienceError(caught instanceof Error
        ? caught.message
        : 'We couldn’t check your family circles.');
    } finally {
      setAudienceLoading(false);
    }
  };

  const openAudienceStep = () => {
    setStep('audience');
    void refreshAudiences();
  };

  const share = async () => {
    if (!file || selected.length === 0 || progress !== null) return;
    setProgress(0);
    setError(null);
    try {
      const result = await api.uploadStory(file, selected, caption, setProgress);
      onCreated(result.story);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not share this story.');
      setProgress(null);
    }
  };

  const chooseAnother = () => {
    setFile(null);
    setRecorderOpen(false);
    setStep('pick');
  };

  const openNativeVideoCapture = () => {
    setRecorderOpen(false);
    window.setTimeout(() => videoInput.current?.click(), 0);
  };

  const stepNumber = step === 'pick' ? '1' : step === 'review' ? '2' : '3';
  const composerTitle = recorderOpen
    ? 'Record a video'
    : step === 'pick'
      ? 'Share a moment'
      : step === 'review'
        ? 'Make it yours'
        : 'Choose your people';

  return (
    <div className="story-composer-backdrop" role="presentation">
      <section className="story-composer" role="dialog" aria-modal="true" aria-labelledby="story-compose-title">
        <input ref={photoInput} hidden type="file" accept="image/*" capture="environment" onChange={(event) => { choose(event.target.files); event.currentTarget.value = ''; }} />
        <input ref={videoInput} hidden type="file" accept="video/*" capture="environment" onChange={(event) => { choose(event.target.files); event.currentTarget.value = ''; }} />
        <input ref={libraryInput} hidden type="file" accept="image/*,video/*" onChange={(event) => { choose(event.target.files); event.currentTarget.value = ''; }} />

        <header className="story-composer-head">
          {recorderOpen
            ? <button type="button" onClick={() => setRecorderOpen(false)} aria-label="Back to story choices"><BackIcon /></button>
            : step === 'pick'
              ? <span className="story-step">New story</span>
              : <button type="button" onClick={() => setStep(step === 'audience' ? 'review' : 'pick')} aria-label="Go back"><BackIcon /></button>}
          <div><small>Step {stepNumber} of 3</small><h2 id="story-compose-title">{composerTitle}</h2></div>
          <button type="button" onClick={onClose} aria-label="Close story composer"><CloseIcon /></button>
        </header>

        <div className="story-composer-body">
          {recorderOpen ? (
            <VideoRecorder
              onCapture={(recording) => {
                setRecorderOpen(false);
                chooseFile(recording, setFile, setStep, setError);
              }}
              onUseDeviceCamera={openNativeVideoCapture}
              filePrefix="story"
            />
          ) : <>
            {error && <div className="banner error"><p>{error}</p></div>}
            {step === 'pick' && (
            <div className="story-capture-grid">
              <button type="button" className="story-capture-primary video" onClick={() => setRecorderOpen(true)}>
                <span><VideoIcon /></span><strong>Record video</strong><small>Record without leaving the app</small>
              </button>
              <button type="button" className="story-capture-primary photo" onClick={() => photoInput.current?.click()}>
                <span><CameraIcon /></span><strong>Take photo</strong><small>Use your phone’s camera</small>
              </button>
              <button type="button" className="story-library-button" onClick={() => libraryInput.current?.click()}>
                <span className="story-library-stack"><i /><i /><i /></span>
                <span><strong>Choose from library</strong><small>Use something you already captured</small></span><b>›</b>
              </button>
              <p className="story-capture-note">Record and preview videos here, or choose something you already captured.</p>
            </div>
          )}

          {step === 'review' && file && preview && (
            <div className="story-review">
              <div className="story-review-media">
                {file.type.startsWith('video/') ? <InlineVideoPreview src={preview} /> : <img src={preview} alt="Story preview" />}
                <button type="button" onClick={chooseAnother}>Choose another</button>
              </div>
              <label className="story-caption-field">
                <span>Say something <small>Optional</small></span>
                <textarea rows={3} maxLength={500} value={caption} placeholder="What’s happening?" onChange={(event) => setCaption(event.target.value)} />
                <small>{caption.length}/500 · {formatBytes(file.size)}</small>
              </label>
            </div>
          )}

          {step === 'audience' && (
            <div className="story-audience-step">
              <div className="story-once-note"><span>1×</span><div><strong>One original, however many families</strong><p>The media is stored once in Nextcloud. These choices grant access without duplicating the file.</p></div></div>
              <fieldset>
                <legend>Who should see this?</legend>
                {audienceLoading ? (
                  <div className="story-audience-status" role="status">
                    <div className="spinner" aria-hidden="true" />
                    <span><strong>Checking your circles</strong><small>Your recording is safe while we refresh the list.</small></span>
                  </div>
                ) : audienceError ? (
                  <div className="story-audience-status error" role="alert">
                    <span><strong>We couldn’t load your circles</strong><small>{audienceError} Your recording is still here.</small></span>
                    <button type="button" className="btn btn-sm" onClick={() => void refreshAudiences()}>Try again</button>
                  </div>
                ) : availableAudiences.length === 0 ? (
                  <div className="story-audience-status empty">
                    <span><strong>No circles are available right now</strong><small>Check again before leaving this story.</small></span>
                    <button type="button" className="btn btn-sm" onClick={() => void refreshAudiences()}>Check again</button>
                  </div>
                ) : availableAudiences.map((audience) => (
                  <label className={selected.includes(audience.id) ? 'selected' : ''} key={audience.id}>
                    <input type="checkbox" checked={selected.includes(audience.id)} onChange={() => toggle(audience.id)} />
                    <span className="story-audience-check">{selected.includes(audience.id) ? '✓' : initials(audience.label)}</span>
                    <span><strong>{audience.label}</strong><small>Replies and reactions stay in this family</small></span>
                  </label>
                ))}
              </fieldset>
              {selected.length > 1 && <p className="story-thread-note">People receive the same story, but each family’s response thread remains private.</p>}
            </div>
          )}
          </>}
        </div>

        {!recorderOpen && step !== 'pick' && (
          <footer className="story-composer-actions">
            {step === 'review' ? <button type="button" className="btn btn-primary btn-block" onClick={openAudienceStep}>Next</button> : (
              <button type="button" className="btn btn-primary btn-block" disabled={audienceLoading || Boolean(audienceError) || selected.length === 0 || progress !== null} onClick={() => void share()}>
                {audienceLoading
                  ? 'Checking family circles…'
                  : progress === null
                    ? `Share with ${selected.length || 'your'} ${selected.length === 1 ? 'family' : 'families'}`
                    : `Preparing & uploading ${Math.round(progress * 100)}%`}
              </button>
            )}
          </footer>
        )}
      </section>
    </div>
  );
}

function chooseFile(
  chosen: File,
  setFile: (file: File) => void,
  setStep: (step: 'review') => void,
  setError: (error: string | null) => void,
) {
  if (chosen.size > 512 * 1024 * 1024) {
    setError('That video is larger than 512 MB. Shorten it and try again.');
    return;
  }
  setFile(chosen);
  setStep('review');
  setError(null);
}

function InlineVideoPreview({ src }: { src: string }) {
  return (
    <div className="story-inline-video">
      <AutoplayVideo
        src={src}
        loop
        label="Recorded story preview"
      />
    </div>
  );
}

function StoryViewer({ detail, position, total, onClose, onPrevious, onNext, onAudienceChange, onThreadChange, onViewed }: {
  detail: StoryDetail;
  position: number;
  total: number;
  onClose: () => void;
  onPrevious?: () => void;
  onNext: () => void;
  onAudienceChange: (audienceId: string) => void;
  onThreadChange: (thread: StoryThread) => void;
  onViewed: (storyId: string, audienceId: string) => void;
}) {
  const { story, thread, selectedAudienceId } = detail;
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const selectedAudience = story.audiences.find((audience) => audience.id === selectedAudienceId) ?? story.audiences[0];

  useEffect(() => {
    if (!selectedAudience) return;
    void api.markStoryViewed(story.id, selectedAudience.id).then(() => onViewed(story.id, selectedAudience.id)).catch(() => undefined);
  }, [onViewed, selectedAudience, story.id]);

  const react = async (reaction: string) => {
    if (!selectedAudience || sending) return;
    setSending(true);
    setActionError(null);
    try {
      const mine = thread.reactions.find((item) => item.reacted)?.reaction;
      const result = await api.setStoryReaction(story.id, selectedAudience.id, mine === reaction ? null : reaction);
      onThreadChange(result.thread);
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : 'Could not react.');
    } finally {
      setSending(false);
    }
  };

  const sendReply = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedAudience || !reply.trim() || sending) return;
    setSending(true);
    setActionError(null);
    try {
      const result = await api.replyToStory(story.id, selectedAudience.id, reply.trim());
      onThreadChange(result.thread);
      setReply('');
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : 'Could not send reply.');
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="story-viewer" role="dialog" aria-modal="true" aria-label={`${story.authorName}’s story`}>
      <div className="story-viewer-backdrop" style={{ backgroundImage: `url(${story.previewUrl}?size=320)` }} />
      <div className="story-progress" aria-label={`Story ${Math.max(position + 1, 1)} of ${Math.max(total, 1)}`}>
        {Array.from({ length: Math.max(total, 1) }, (_, index) => <span className={index <= position ? 'complete' : ''} key={index}><i /></span>)}
      </div>
      <header className="story-viewer-head">
        <span className="story-viewer-avatar">{initials(story.authorName)}</span>
        <span><strong>{story.isMine ? 'Your story' : story.authorName}</strong><small>{timeAgo(story.createdAt)}</small></span>
        <button type="button" onClick={onClose} aria-label="Close story"><CloseIcon /></button>
      </header>

      <div className="story-viewer-media">
        {story.isVideo
          ? <AutoplayVideo src={story.mediaUrl} poster={`${story.previewUrl}?size=1200`} onEnded={onNext} label={`${story.authorName}’s story video`} />
          : <img src={`${story.previewUrl}?size=2048`} alt={story.caption || story.name} />}
      </div>
      {onPrevious && <button type="button" className="story-nav-zone previous" onClick={onPrevious} aria-label="Previous story" />}
      <button type="button" className="story-nav-zone next" onClick={onNext} aria-label="Next story" />

      <div className="story-viewer-bottom">
        {story.caption && <p className="story-viewer-caption"><strong>{firstName(story.authorName)}</strong> {story.caption}</p>}
        {story.audiences.length > 1 && (
          <div className="story-audience-pills" aria-label="Response thread">
            {story.audiences.map((audience) => <button type="button" className={audience.id === selectedAudienceId ? 'active' : ''} key={audience.id} onClick={() => onAudienceChange(audience.id)}>{audience.label}</button>)}
          </div>
        )}
        {selectedAudience && <span className="story-replying-label">Replies stay in {selectedAudience.label}</span>}
        <div className="story-reactions" aria-label="React to story">
          {REACTIONS.map((reaction) => {
            const state = thread.reactions.find((item) => item.reaction === reaction);
            return <button type="button" className={state?.reacted ? 'active' : ''} key={reaction} disabled={sending} onClick={() => void react(reaction)}><span>{reaction}</span>{state && <small>{state.count}</small>}</button>;
          })}
        </div>
        {thread.replies.length > 0 && <div className="story-replies">{thread.replies.slice(-3).map((item) => <p key={item.id}><strong>{item.isOwn ? 'You' : firstName(item.displayName)}</strong> {item.message}</p>)}</div>}
        {actionError && <p className="story-action-error" role="alert">{actionError}</p>}
        <form className="story-reply-form" onSubmit={(event) => void sendReply(event)}>
          <input value={reply} maxLength={1000} onChange={(event) => setReply(event.target.value)} placeholder={`Reply in ${selectedAudience?.label ?? 'family'}…`} aria-label="Reply to story" />
          <button type="submit" disabled={!reply.trim() || sending} aria-label="Send reply"><SendIcon /></button>
        </form>
        {story.isMine && thread.seenBy.length > 0 && <details className="story-seen"><summary>Seen by {thread.seenBy.length}</summary><p>{thread.seenBy.map((person) => person.displayName).join(', ')}</p></details>}
      </div>
    </section>
  );
}

function initials(name: string): string {
  return name.trim().split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase() ?? '').join('') || '?';
}

function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] || name;
}

function timeAgo(timestamp: number): string {
  const minutes = Math.max(0, Math.floor((Date.now() - timestamp) / 60_000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`;
}
